<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('productSerial.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('productSerial.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <div class="row">
      <div class="col-md-3">
        <div class="form-group">
          <ts-card>
            <ts-card-wrapper>
              <ts-loading-banner :loading="loading">
                <form>
                  <fieldset>
                    <legend>{{ $t('productSerial.criteria') }}</legend>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label class="required">{{
                            $t('productSerial.date')
                          }}</label>
                          <a-range-picker
                            v-model="model.date_range"
                            format="DD-MM-YYYY"
                            valueFormat="DD-MM-YYYY"
                          />
                          <div class="validate" v-if="errors.has('date_range')">
                            {{ errors.first('date_range') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label class="required">{{
                            $t('productSerial.branch')
                          }}</label>
                          <a-select
                            v-model="model.branch_id"
                            :options="branchList"
                             @change="onBranchChange"
                            class="ts-w-full"
                          ></a-select>
                          <div
                            class="validate"
                            v-if="errors.has('branch_id')"
                          >
                            {{ errors.first('branch_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label class="required">{{
                            $t('productSerial.warehouse')
                          }}</label>
                          <a-select
                            v-model="model.location_id"
                            :options="locations"
                             @change="clearProduct"
                            class="ts-w-full"
                          ></a-select>
                          <div
                            class="validate"
                            v-if="errors.has('location_id')"
                          >
                            {{ errors.first('location_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{ $t('productSerial.category') }}</label>
                          <a-select
                            v-model="model.category_id"
                            :options="categories"
                            showSearch
                            @change="clearProduct"
                            :filterOption="(input, option) => option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0"
                            class="ts-w-full"
                            placeholder="All"
                          ></a-select>
                          <div class="validate" v-if="errors.has('category_id')">
                            {{ errors.first('category_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{ $t('productSerial.group') }}</label>
                          <a-select
                            v-model="model.pro_group_id"
                            :options="groups"
                            showSearch
                            @change="clearProduct"
                            :filterOption="(input, option) => option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0"                            
                            class="ts-w-full"
                            placeholder="All"
                          ></a-select>
                          <div class="validate" v-if="errors.has('pro_group_id')">
                            {{ errors.first('pro_group_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12 form-group">
                        <label>{{ $t('productSerial.product') }}</label>
                        <a-select
                          v-model="model.product_list_id"
                          mode="multiple"
                          :loading="on_search_product"
                          @search="onProductSearch"
                          :disabled="showSearchProduct"
                          :show-arrow="false"
                          :filter-option="true"
                          :not-found-content="null"
                           :filterOption="
                              (input, option) =>
                                option.componentOptions.children[0].text
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                            "
                        >
                            <a-select-option 
                              v-for="product in productList" 
                              :key="product.value"
                              :value="product.value"
                            >
                              {{ product.label }}
                            </a-select-option>
                        </a-select>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label class="required">{{ $t('productSerial.status') }}</label>
                          <a-select
                            v-model="model.sn_status_id"
                            :options="sn_status"
                            class="ts-w-full"
                          >
                          </a-select>
                          <div class="validate" v-if="errors.has('sn_status_id')">
                            {{ errors.first('sn_status_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-6 text-right">
                        <ts-button
                          class="btn-block"
                          outline
                          color="success"
                          @click.prevent="exportExcel"
                          :waiting="exporting"
                          ><i class="fas fa-file-excel"></i>
                          {{ $t('excel') }}</ts-button
                        >
                      </div>
                      <div class="col-md-6 text-right">
                        <ts-button
                          class="btn-block"
                          color="primary"
                          @click.prevent="preview"
                          :waiting="waiting"
                          ><i class="fas fa-file-pdf text-red"></i>
                          {{ $t('preview') }}</ts-button
                        >
                      </div>
                    </div>
                  </fieldset>
                </form>
              </ts-loading-banner>
            </ts-card-wrapper>
          </ts-card>
        </div>
      </div>
      <div class="col-md-9">
        <ts-card>
          <ts-card-wrapper>
            <form>
              <fieldset>
                <legend>{{ $t('productSerial.preview') }}</legend>
                <ts-preview-pdf v-model="waiting" :src="src" />
              </fieldset>
            </form>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import { mapActions } from 'vuex'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'

export default {
  name: 'productSerial',
  data () {
    return {
      waiting: false,
      loading: false,
      exporting: false,
      errors: new Errors(),
      locations: [],
      groups: [],
      categories: [],
      sn_status: [],
      on_search_product: false,
      productList: [],
      branchList: [],
      src: '',
      model: {
        date_range: [moment().format('DD-MM-YYYY'), moment().format('DD-MM-YYYY')],
        sn_status_id: 1,
        pro_group_id: undefined,
        category_id: undefined,
        location_id: null,
        branch_id: '',
        product_list_id: []
      }
    }
  },
  computed: {
    showSearchProduct() {
      return this.model.location_id == '' || this.model.location_id == undefined
    }
  },
  created(){
    this.model.branch_id = this.$store.state.authUser.user.default_branch.branch_id
  },
  methods: {
    ...mapActions('report/resource', [
      'productGroup', 
      'productCategory', 
      'getLocation', 
      'getSerialStatus',
      'productProfileLocation'
    ]),
    moment,
    async fetchResources () {
      this.loading = true
      let snStatus = await this.getSerialStatus()
      await this.fetchLocation()
      await this.productGroup()
        .then(response => {
          this.groups = response.data.map(el => ({
            value: el.pro_group_id,
            label: el.pro_group_name_en
          }))
        })
      await this.$store
        .dispatch('security/user/getBranchAssigned')
        .then(response => {
          this.branchList = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
        })
      await this.productCategory()
        .then(response => {
          this.categories = response.data.map(el => ({
            value: el.category_id,
            label: el.category_name_en
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
      this.sn_status = snStatus.data.map(el => ({
        value: el.sn_status_id,
        label: el.sn_status,
      }))
    },
    preview () {
      this.errors = new Errors()
      this.waiting = true
      this.src = ''
      this.$store
        .dispatch('report/inventory/productSerial', this.model)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    onProductSearch: _.debounce(function(value) {
      this.on_search_product = true
      this.productProfileLocation({
        filter: {
          location_id: this.model.location_id,
          pro_group_id: this.model.pro_group_id,
          category_id: this.model.category_id,
          search: value
        }
      })
      .then(response => {
        let product = response.data.map(el => ({
          value: el.product_id,
          label: el.product_name_en
        }))

        let diff = _.differenceBy(...product, this.productList,'value')

        if(!diff.length > 0){
           this.productList.push(...product)
        }else {
          this.productList.push(...diff)
        }

        this.productList = _.uniqBy(this.productList, 'value').splice(0, 300)
        
      })
      .catch(error => {
        this.$toasted.error(error.message)
      })
      .finally(() => {
        this.on_search_product = false
      })
    }, 500),
    async onBranchChange(value){
      this.loading = true
      this.model.location_id = null
      this.model.product_list_id = []
      await this.fetchLocation()
      this.loading = false
    },
    clearProduct(){
      this.model.product_list_id = []
      this.productList = []
    },
    async fetchLocation(){
      await this.getLocation({
        branch_id: this.model.branch_id
      })
      .then(response => {
        this.locations = response.data.map(el => ({
          value: el.location_id,
          label: el.location_name_en
        }))
      })
      .catch(error => {
        this.$toasted.error(error.message)
      })
    },
    exportExcel () {
      this.exporting = true
      this.errors = new Errors()

      this.$store
        .dispatch('report/inventory/productSerialExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    },
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchResources()
    })
  }
}
</script>
